import React, { useEffect, useState, useMemo } from "react";
import { Modal, Form, Select, Button, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getRoles } from "../../../redux/slices/userManagementSlice";
import { updateContentRoles } from "../../../redux/slices/trainAndContentSlice";

const EditRolesModal = ({
  visible,
  onClose,
  content,
  onSuccess,
  page: currentPage,
  limit: currentLimit,
  fileType,
  searchQuery,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const { getRolesData } = useSelector((state) => state.userManagementReducer);
  const globalPage = useSelector((state) => state.globalReducer?.page);
  const globalLimit = useSelector((state) => state.globalReducer?.limit);
  const globalSearchInput = useSelector(
    (state) => state.globalReducer?.searchInput
  );

  const allRoles = getRolesData?.data ?? [];
  const roleOptions = useMemo(
    () =>
      allRoles.map((role) => ({
        value: role._id,
        label: role.name,
      })),
    [allRoles]
  );

  const initialRoleIds = useMemo(() => {
    if (!content) return [];
    if (content.roleIds && Array.isArray(content.roleIds) && content.roleIds.length > 0) {
      return content.roleIds;
    }
    if (content.role && Array.isArray(content.role) && allRoles.length > 0) {
      return content.role
        .map((roleName) => {
          const r = allRoles.find(
            (item) => item.name === roleName || item._id === roleName
          );
          return r?._id;
        })
        .filter(Boolean);
    }
    return [];
  }, [content, allRoles]);

  useEffect(() => {
    if (visible) {
      dispatch(getRoles({ navigate }));
    }
  }, [visible, dispatch, navigate]);

  useEffect(() => {
    if (visible && content) {
      setSelectedRoleIds(initialRoleIds);
      form.setFieldsValue({ roleIds: initialRoleIds });
    }
  }, [visible, content, initialRoleIds, form]);

  const handleSave = async () => {
    if (!content?._id) return;
    setSaving(true);
    try {
      await dispatch(
        updateContentRoles({
          contentId: content._id,
          roleIds: selectedRoleIds,
          page: currentPage ?? globalPage ?? 1,
          limit: currentLimit ?? globalLimit ?? 10,
          fileType: fileType || content?.type,
          navigate,
          searchQuery: searchQuery ?? globalSearchInput ?? "",
        })
      ).unwrap();
      message.success("Roles updated successfully");
      onSuccess?.();
      onClose();
    } catch (err) {
      // Error already handled in thunk (toast)
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedRoleIds([]);
    onClose();
  };

  const contentName =
    content?.fileName?.replace?.(/\.(pdf|txt|doc|csv|xlsx)$/i, "") ||
    content?.fileName ||
    "Content";

  return (
    <Modal
      title="Edit Roles"
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          onClick={handleSave}
        >
          Save
        </Button>,
      ]}
      destroyOnClose
      width={480}
      centered
      styles={{
        body: {
          maxHeight: "60vh",
          overflow: "hidden",
        },
      }}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item label="Content name">
          <span style={{ fontWeight: 500 }}>{contentName}</span>
        </Form.Item>
        <Form.Item
          name="roleIds"
          label="Roles"
          rules={[{ required: false }]}
        >
          <Select
            mode="multiple"
            placeholder="Select roles"
            options={roleOptions}
            value={selectedRoleIds}
            onChange={(values) => {
              setSelectedRoleIds(values ?? []);
              form.setFieldsValue({ roleIds: values ?? [] });
            }}
            style={{ width: "100%" }}
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditRolesModal;
